/**
 * ct-1028.js — Elementos a medida del tema 1028.
 *
 * Sustituye a `secondary.js` (código ofuscado de un tercero que estaba MUERTO en
 * producción: declaraba otra vez `ON_CHANGE_DEBOUNCE_TIMER`, que Dawn ya declara en
 * `global.js`, y un SyntaxError tumba el archivo entero antes de ejecutar nada).
 *
 * Aquí solo se implementan los elementos que estaban muertos y SÍ tienen HTML en las
 * plantillas vivas:
 *   - <sticky-atc>                        → sections/main-product.liquid (bloque sticky_atc)
 *   - <secondary-variant-select>          → dentro del sticky
 *   - <secondary-variant-select-separate> → misma pieza, otro nombre de etiqueta
 *   - <internal-video>                    → sections/testimonials.liquid, snippets/video-player.liquid
 *
 * Diagnóstico desde la consola (sin necesidad de leer estilos calculados):
 *   - `data-ct-ready`  en los cuatro elementos → `true` | `no-options` | `no-video`
 *   - `data-ct-state`  en <sticky-atc>        → `visible` | `hidden`
 *   - `data-ct-anchor` en <sticky-atc>        → `ok` | `missing`
 *   - `data-ct-mode`   en los selectores      → `combined` | `per-option`
 *   - `data-ct-debug`  en <sticky-atc>        → los números con los que decide
 *     (ronda de diagnóstico; se quita cuando la barra esté dada por buena)
 *     `bar.ctDebug()` refresca esa cadena con la geometría de ahora mismo.
 *
 * Reglas de este archivo:
 *   - Todo vive dentro de este IIFE. NO se declara ni una sola constante global.
 *   - Cada customElements.define() va con su guard, para no pisar nunca nada de Dawn.
 *   - Ningún texto visible se escribe aquí: sale del propio HTML o de
 *     window.variantStrings (que theme.liquid rellena con las traducciones).
 *   - Nada revienta si falta un elemento: todo se comprueba antes de usarse.
 */
(function () {
  'use strict';

  if (!window.customElements) return;

  /* ------------------------------------------------------------------ *
   * Utilidades compartidas
   * ------------------------------------------------------------------ */

  /** Eventos de Dawn (assets/constants.js). Si no cargó, trabajamos sin pub/sub. */
  var EVENTS = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
  var HAS_PUBSUB = !!EVENTS && typeof subscribe === 'function';

  /** Suscribe a un evento de Dawn y devuelve siempre una función para desuscribir. */
  function on(eventName, callback) {
    if (!HAS_PUBSUB || !eventName) return function () {};
    return subscribe(eventName, callback);
  }

  /** Colapsa espacios y saltos de línea del Liquid en un texto de una sola línea. */
  function clean(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  /** El <variant-selects> de Dawn de esa sección: la única fuente de verdad del producto. */
  function findMainPicker(sectionId) {
    var pickers = document.querySelectorAll('variant-selects');
    for (var i = 0; i < pickers.length; i++) {
      if (pickers[i].dataset.section === sectionId) return pickers[i];
    }
    return null;
  }

  function findStickyBar(sectionId) {
    var bars = document.querySelectorAll('sticky-atc');
    for (var i = 0; i < bars.length; i++) {
      if (bars[i].dataset.section === sectionId) return bars[i];
    }
    return null;
  }

  /**
   * Devuelve un grupo por cada opción del producto, en el orden del DOM.
   * Sirve igual para el picker de Dawn (desplegable, swatch o pill) y para el
   * picker secundario del sticky, porque los dos envuelven cada opción en
   * `.product-form__input`.
   */
  function optionGroups(root) {
    if (!root) return [];
    var groups = [];
    root.querySelectorAll('.product-form__input').forEach(function (wrapper) {
      var select = wrapper.querySelector('select');
      if (select) {
        groups.push({ type: 'select', control: select, items: Array.from(select.options) });
        return;
      }
      var radios = Array.from(wrapper.querySelectorAll('input[type="radio"]'));
      if (radios.length) groups.push({ type: 'radio', control: radios[0], items: radios });
    });
    return groups;
  }

  /** Índice del valor elegido dentro de su grupo, o -1. */
  function selectedIndex(group) {
    if (!group) return -1;
    if (group.type === 'select') return group.control.selectedIndex;
    for (var i = 0; i < group.items.length; i++) {
      if (group.items[i].checked) return i;
    }
    return -1;
  }

  /**
   * Escribe los valores de opción en un picker (por VALOR, no por posición: así no
   * asumimos que dos pickers listan las opciones en el mismo orden).
   * Devuelve si cambió algo y el último control tocado, para poder disparar
   * un `change` real con un `event.target` que Dawn sepa leer.
   */
  function applyOptionValues(picker, values) {
    var groups = optionGroups(picker);
    var changed = false;
    var last = null;

    groups.forEach(function (group, index) {
      var value = values[index];
      if (value == null) return;
      var match = group.items.find(function (item) {
        return item.value === value;
      });
      if (!match) return;

      if (group.type === 'select') {
        last = group.control;
        if (group.control.value !== value) {
          group.control.value = value;
          changed = true;
        }
      } else {
        last = match;
        if (!match.checked) {
          match.checked = true;
          changed = true;
        }
      }
    });

    return { changed: changed, last: last };
  }

  /** Pone el id de variante en los formularios de compra de la sección. */
  function writeVariantId(sectionId, variantId) {
    if (!variantId) return;
    var value = String(variantId);

    ['product-form-' + sectionId, 'product-form-installment-' + sectionId].forEach(function (formId) {
      var form = document.getElementById(formId);
      var input = form && form.querySelector('input[name="id"]');
      if (!input || input.value === value) return;
      input.value = value;
      // Dawn dispara este change para que se recalculen reglas de cantidad y precio por unidad.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    var bar = findStickyBar(sectionId);
    if (bar) {
      bar.querySelectorAll('.sticky-atc-variant-id').forEach(function (input) {
        if (input.value !== value) input.value = value;
      });
    }
  }

  /**
   * Escribe el id de variante AHORA y otra vez en el siguiente tick.
   *
   * Por qué el segundo pase: el bloque sticky de `main-product.liquid` trae un
   * <script> heredado que, al cargar la página y en cada `change` del picker
   * secundario, copia el `value` del <select> a TODOS los `input[name="id"]`
   * ocultos del documento. Según qué picker renderice el bloque, ese value puede
   * no ser un id de variante válido (o ser el de otra variante), y entonces el
   * formulario principal queda sin poder añadir al carrito. Ese script se
   * registra en DOMContentLoaded, o sea DESPUÉS que nosotros, así que corre
   * después: el `setTimeout(…, 0)` nos deja la última palabra.
   */
  var reassertTimers = {};
  function commitVariantId(sectionId, variantId) {
    if (!variantId) return;
    writeVariantId(sectionId, variantId);
    clearTimeout(reassertTimers[sectionId]);
    reassertTimers[sectionId] = setTimeout(function () {
      writeVariantId(sectionId, variantId);
    }, 0);
  }

  /** Contadores de diagnóstico de la barra (ver §10 de JS-CT-1028.md). */
  function newDebug() {
    return { n: 0, ev: 0, raf: 0, io: 0, dev: 0, conn: 0, disc: 0, via: '-', why: '-', scroller: '-' };
  }

  /* ------------------------------------------------------------------ *
   * <sticky-atc> — barra de compra fija
   * ------------------------------------------------------------------ */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      // --- INSTRUMENTACIÓN (ronda de diagnóstico) ---------------------------
      // Contadores en crudo, sin tocar ninguna decisión. Se leen con
      // `bar.dataset.ctDebug` o llamando a `bar.ctDebug()` desde la consola.
      // `conn`/`disc` cuentan conexiones y desconexiones del elemento: si el
      // nodo se re-inserta en el DOM, connectedCallback sale por el guard de
      // `ready` y los listeners quedarían sin reenganchar. Hay que saberlo.
      if (!this.dbg) this.dbg = newDebug();
      this.dbg.conn += 1;

      if (this.ready) return;
      this.ready = true;

      this.sectionId = this.dataset.section || '';
      this.variantInputs = this.querySelectorAll('.sticky-atc-variant-id');
      this.image = document.getElementById('sticky-atc-image-' + this.sectionId);
      this.priceEl = document.getElementById('sticky-atc-price-' + this.sectionId);
      this.button = this.querySelector('.sticky-atc__button button') || this.querySelector('button[name="add"]');
      this.label = this.querySelector('.sticky-atc__label');
      this.labelText = this.querySelector('.sticky-atc__label__text');
      this.errorEl = this.querySelector('.sticky-atc__error');
      this.variantLink = this.querySelector('.sticky-atc__variant');

      // Texto del botón cuando hay stock: se guarda tal cual lo dejó el Liquid
      // (el ajuste "button_label" del bloque), nunca se escribe aquí.
      this.defaultLabel = this.labelText ? this.labelText.textContent : null;

      this.currentVariantId = this.readInitialVariantId();
      this.capturePriceSuffix();
      this.setupVisibility();
      this.setupVariantLink();

      this.unsubscribers = [
        on(EVENTS && EVENTS.variantChange, this.onVariantChange.bind(this)),
        on(EVENTS && EVENTS.cartError, this.onCartError.bind(this)),
        on(EVENTS && EVENTS.cartUpdate, this.clearError.bind(this)),
      ];

      // El script heredado corrompe el id al cargar; lo reafirmamos justo después.
      if (document.readyState === 'loading') {
        document.addEventListener(
          'DOMContentLoaded',
          function () {
            commitVariantId(this.sectionId, this.currentVariantId);
          }.bind(this),
          { once: true }
        );
      } else {
        commitVariantId(this.sectionId, this.currentVariantId);
      }

      // --- INSTRUMENTACIÓN: dos sondas más, para saber QUÉ mecanismo vive ---
      // Si nunca aparece `via=init-raf`, requestAnimationFrame no corre en esta
      // pestaña (y entonces tampoco corren los callbacks del observer, que se
      // entregan en el mismo punto del ciclo de repintado).
      // Si nunca aparece `via=init-late`, es que ni los temporizadores corren.
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(
          function () {
            this.dbg.raf += 1;
            this.evaluate('init-raf');
          }.bind(this)
        );
      }
      setTimeout(
        function () {
          this.evaluate('init-late');
        }.bind(this),
        500
      );

      // Diagnóstico: confirma desde la consola que el elemento se inicializó de verdad.
      this.setAttribute('data-ct-ready', 'true');
    }

    disconnectedCallback() {
      if (this.dbg) this.dbg.disc += 1;
      if (this.observer) this.observer.disconnect();
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      if (this.onViewportChange) {
        window.removeEventListener('scroll', this.onViewportChange);
        window.removeEventListener('resize', this.onViewportChange);
      }
      if (this.onAnyScroll) document.removeEventListener('scroll', this.onAnyScroll, true);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      (this.unsubscribers || []).forEach(function (off) {
        if (typeof off === 'function') off();
      });
      this.unsubscribers = [];
    }

    /** El id bueno de arranque: el del formulario principal, aún sin tocar por nadie. */
    readInitialVariantId() {
      var form = document.getElementById('product-form-' + this.sectionId);
      var input = form && form.querySelector('input[name="id"]');
      if (input && input.value) return input.value;
      return this.variantInputs[0] ? this.variantInputs[0].value : null;
    }

    /* ---------------- visibilidad ---------------- */

    setupVisibility() {
      this.setVisible(false);
      this.findAnchor();

      // Si el ancla aún no estaba en el DOM cuando se actualizó el elemento
      // (apps que inyectan HTML, secciones que se repintan), se reintenta.
      if (!this.anchor) {
        var retry = this.findAnchor.bind(this);
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', retry, { once: true });
        }
        window.addEventListener('load', retry, { once: true });
      }

      // Red de seguridad: una sola medición por frame (no es un listener de scroll
      // "a pelo": va estrangulado con requestAnimationFrame y es passive). El
      // IntersectionObserver sigue siendo el motor; esto solo garantiza que el
      // estado nunca se quede caducado si el observer no entrega su callback.
      var self = this;
      this.onViewportChange = function (event) {
        self.dbg.ev += 1;
        self.dbg.lastEvent = event && event.type ? event.type : 'scroll';
        if (self.frame) return;
        self.frame = window.requestAnimationFrame(function () {
          self.frame = 0;
          self.dbg.raf += 1;
          self.evaluate(self.dbg.lastEvent);
        });
      };
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });

      // --- INSTRUMENTACIÓN: ¿quién hace scroll de verdad? -------------------
      // En fase de captura para enterarnos también de los scroll de ELEMENTOS
      // (esos no burbujean). Solo cuenta y apunta el nombre del que scrollea;
      // no decide nada. Si `dev` sube mientras `ev` se queda a cero, el scroll
      // real no es el de la ventana.
      this.onAnyScroll = function (event) {
        self.dbg.dev += 1;
        var target = event && event.target;
        if (!target || target === document || target === window || target === document.documentElement) {
          self.dbg.scroller = 'document';
        } else if (target.tagName) {
          self.dbg.scroller = target.tagName.toLowerCase() + '.' + String(target.className || '').split(' ')[0];
        } else {
          self.dbg.scroller = 'otro';
        }
      };
      document.addEventListener('scroll', this.onAnyScroll, true);

      // En el editor de temas el comerciante selecciona el bloque: hay que enseñárselo.
      this.onBlockSelect = function (event) {
        if (event.target === this || this.contains(event.target)) this.setVisible(true);
      }.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    /** Ancla: el botón de compra principal. Se expone en `data-ct-anchor`. */
    findAnchor(via) {
      if (this.anchor && this.anchor.isConnected) return;

      var form = document.getElementById('product-form-' + this.sectionId);
      this.anchor =
        document.getElementById('ProductSubmitButton-' + this.sectionId) ||
        (form || document).querySelector('.product-form__buttons');

      this.setAttribute('data-ct-anchor', this.anchor ? 'ok' : 'missing');
      if (this.anchor) this.observeAnchor();
      this.evaluate(via || 'init');
    }

    observeAnchor() {
      if (this.observer || !('IntersectionObserver' in window)) return;

      /*
       * `rootMargin` estira el root MUY por debajo del viewport. Con eso,
       * "no interseca" solo puede significar UNA cosa: el ancla ya quedó por
       * encima de la pantalla.
       *
       * Por qué hacía falta: con el root normal, el ancla está fuera de
       * intersección tanto por arriba como por abajo. Si la página salta de un
       * lado al otro de una sola vez (un window.scrollTo largo, un enlace con
       * ancla, restaurar la posición al recargar), `isIntersecting` vale false
       * antes y después: NO se cruza ningún umbral y el observer NO dispara.
       * La barra se quedaba escondida para siempre. Estirando el root, ese salto
       * pasa de intersecar a no intersecar y el callback sí llega.
       */
      var self = this;
      this.observer = new IntersectionObserver(
        function () {
          self.dbg.io += 1;
          self.evaluate('io');
        },
        { threshold: 0, rootMargin: '0px 0px 100000px 0px' }
      );
      this.observer.observe(this.anchor);
    }

    /**
     * Decide con geometría FRESCA (no con la del entry, que puede llegar caducada).
     * Nunca mira si el botón está habilitado: un producto agotado enseña la barra igual.
     */
    evaluate(via) {
      if (!this.dbg) this.dbg = newDebug();
      this.dbg.n += 1;
      if (via) this.dbg.via = via;

      var rect = null;
      if (!this.anchor || !this.anchor.isConnected) {
        this.dbg.why = 'sin-ancla';
        this.setVisible(false);
      } else {
        rect = this.anchor.getBoundingClientRect();
        // Rectángulo de tamaño cero = el ancla está oculta (display:none). Sin esta
        // comprobación `bottom` valdría 0 y la barra se mostraría sin motivo.
        if (rect.width <= 0 && rect.height <= 0) {
          this.dbg.why = 'rect-cero';
          this.setVisible(false);
        } else {
          // Criterio del tema: la barra sale solo cuando el botón principal ya quedó
          // POR ENCIMA del viewport (el cliente lo pasó de largo).
          this.dbg.why = 'geometria';
          this.setVisible(rect.bottom <= 0);
        }
      }
      this.writeDebug(rect);
    }

    /* --- INSTRUMENTACIÓN: el cálculo, escrito en el DOM ------------------- */

    writeDebug(rect) {
      if (!this.dbg) this.dbg = newDebug();
      var d = this.dbg;
      var anchor = 'null';
      if (this.anchor) {
        anchor =
          this.anchor.id ||
          this.anchor.tagName.toLowerCase() + '.' + String(this.anchor.className || '').split(' ')[0];
      }
      var round = function (value) {
        return rect ? Math.round(value) : '?';
      };
      this.setAttribute(
        'data-ct-debug',
        [
          'n=' + d.n,
          'via=' + d.via,
          'conn=' + d.conn,
          'disc=' + d.disc,
          'ev=' + d.ev,
          'raf=' + d.raf,
          'io=' + d.io,
          'dev=' + d.dev,
          'scroller=' + d.scroller,
          'anchor=' + anchor,
          'top=' + round(rect && rect.top),
          'bottom=' + round(rect && rect.bottom),
          'w=' + round(rect && rect.width),
          'h=' + round(rect && rect.height),
          'vh=' + window.innerHeight,
          'sy=' + Math.round(window.scrollY || window.pageYOffset || 0),
          'why=' + d.why,
          '->',
          this.visible ? 'visible' : 'hidden',
        ].join(' ')
      );
    }

    /**
     * Sonda de solo lectura para la consola: `document.querySelector('sticky-atc').ctDebug()`.
     * Refresca `data-ct-debug` con la geometría de AHORA sin cambiar el estado.
     */
    ctDebug() {
      this.writeDebug(this.anchor && this.anchor.isConnected ? this.anchor.getBoundingClientRect() : null);
      return this.getAttribute('data-ct-debug');
    }

    setVisible(visible) {
      if (this.visible === visible) return;
      this.visible = visible;

      // `.visible` es la clase que espera el <style> del bloque para subir la barra.
      this.classList.toggle('visible', visible);

      // Espejo del estado para poder comprobarlo de un vistazo desde la consola.
      // Cambia a la vez que la clase, sin esperar a la transición de 0.3s del CSS,
      // así que es más fiable que leer el `transform` calculado.
      this.setAttribute('data-ct-state', visible ? 'visible' : 'hidden');

      // Escondida está fuera de pantalla pero seguiría siendo tabulable: eso rompe
      // el teclado. `inert` la saca del foco y del lector de pantalla a la vez.
      if ('inert' in HTMLElement.prototype) this.inert = !visible;
      if (visible) this.removeAttribute('aria-hidden');
      else this.setAttribute('aria-hidden', 'true');

      // Alto real de la barra, por si algún CSS quiere reservar hueco abajo.
      document.documentElement.style.setProperty(
        '--sticky-atc-offset',
        visible ? this.offsetHeight + 'px' : '0px'
      );
    }

    /* ---------------- "Change variant" ---------------- */

    setupVariantLink() {
      var link = this.variantLink;
      if (!link) return;

      // Es un <span>: sin esto no se puede alcanzar ni activar con el teclado.
      if (!link.hasAttribute('role')) link.setAttribute('role', 'button');
      if (!link.hasAttribute('tabindex')) link.setAttribute('tabindex', '0');

      link.addEventListener('click', this.goToPicker.bind(this));
      link.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault();
        link.click();
      });
    }

    goToPicker(event) {
      if (event) event.preventDefault();
      var target = findMainPicker(this.sectionId) || document.getElementById('ProductSubmitButton-' + this.sectionId);
      if (!target) {
        window.scrollTo({ top: 0, behavior: scrollBehavior() });
        return;
      }
      target.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      var first = target.querySelector ? target.querySelector('select, input[type="radio"]') : null;
      if (first) first.focus({ preventScroll: true });
    }

    /* ---------------- sincronía con la variante ---------------- */

    onVariantChange(payload) {
      var data = payload && payload.data;
      if (!data || !data.variant || data.sectionId !== this.sectionId) return;
      this.applyVariant(data.variant, data.html);
    }

    /** `html` es el documento que Dawn ya se bajó de la sección; puede no venir. */
    applyVariant(variant, html) {
      if (!variant) return;
      this.currentVariantId = variant.id;
      commitVariantId(this.sectionId, variant.id);
      this.updateImage(variant);
      if (html) {
        this.updatePrice(html);
        this.updateAvailability(html);
      }
      this.clearError();
    }

    updateImage(variant) {
      if (!this.image) return;
      var image = variant.featured_image;
      if (!image || !image.src) return;

      var src = image.src;
      try {
        // Mismo ancho que pide el Liquid, para no bajarse la foto a tamaño original.
        var url = new URL(src, window.location.href);
        url.searchParams.set('width', '500');
        src = url.href;
      } catch (error) {
        /* si la URL no se puede parsear nos quedamos con la original */
      }
      if (this.image.getAttribute('src') !== src) this.image.setAttribute('src', src);
      if (typeof image.alt === 'string') this.image.setAttribute('alt', image.alt);
    }

    /**
     * El precio NO se formatea en JS (cada mercado tiene su moneda y su formato):
     * se copia el que Shopify ya renderizó en el bloque de precio de la sección.
     */
    readPrice(doc) {
      var box = doc.getElementById('price-' + this.sectionId);
      if (!box) return null;
      var node = box.classList.contains('price--on-sale')
        ? box.querySelector('.price__sale .price-item--sale')
        : box.querySelector('.price__regular .price-item--regular');
      if (!node) node = box.querySelector('.price-item');
      return node ? clean(node.textContent) : null;
    }

    /**
     * El span del sticky trae el precio más una decoración del Liquid (" •").
     * La sacamos del propio HTML para poder reponerla sin escribir nada a mano.
     */
    capturePriceSuffix() {
      this.priceSuffix = '';
      if (!this.priceEl) return;
      var rendered = clean(this.priceEl.textContent);
      var price = this.readPrice(document);
      if (price && rendered.indexOf(price) === 0) this.priceSuffix = rendered.slice(price.length);
    }

    updatePrice(html) {
      if (!this.priceEl) return;
      var price = this.readPrice(html);
      if (!price) return;
      this.priceEl.textContent = price + this.priceSuffix;
    }

    updateAvailability(html) {
      if (!this.button) return;
      var fresh = html.getElementById('ProductSubmitButton-' + this.sectionId);
      if (!fresh) return;

      var soldOut = fresh.hasAttribute('disabled');
      this.button.disabled = soldOut;

      if (this.labelText) {
        // Textos traducidos que theme.liquid deja en window.variantStrings,
        // o el label original del bloque. Nada escrito aquí.
        var strings = window.variantStrings || {};
        if (soldOut && strings.soldOut) this.labelText.textContent = strings.soldOut;
        else if (!soldOut && this.defaultLabel != null) this.labelText.textContent = this.defaultLabel;
      }
      if (this.priceEl) this.priceEl.classList.toggle('hidden', soldOut);
    }

    /* ---------------- errores del carrito ---------------- */

    onCartError(payload) {
      if (!this.errorEl || !payload) return;
      if (String(payload.productVariantId) !== String(this.currentVariantId)) return;

      // El texto viene del servidor (ya traducido); nunca lo inventamos.
      var message = typeof payload.errors === 'string' ? payload.errors : payload.message;
      if (typeof message !== 'string' || !message) return;

      this.errorEl.textContent = message;
      this.errorEl.style.display = '';
      if (this.label) this.label.style.display = 'none';
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.style.display = 'none';
      if (this.label) this.label.style.display = '';
    }
  }

  /* ------------------------------------------------------------------ *
   * <secondary-variant-select> / <secondary-variant-select-separate>
   * Selector de variante de la barra sticky.
   * ------------------------------------------------------------------ */

  class SecondaryVariantSelect extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.sectionId = this.dataset.section || '';
      this.groups = optionGroups(this);
      if (!this.groups.length) {
        this.setAttribute('data-ct-ready', 'no-options');
        return;
      }

      // El Liquid puede pintar dos maquetados distintos bajo cualquiera de las dos
      // etiquetas, así que el modo se deduce del HTML, no del nombre del elemento:
      // el <select> único del modo "combinado" lleva la clase `variant-dropdown`
      // y su value es la lista de opciones separada por comas.
      this.combined = !!this.querySelector('select.variant-dropdown');

      this.addEventListener('change', this.onChange.bind(this));
      this.unsubscribe = on(EVENTS && EVENTS.variantChange, this.onVariantChange.bind(this));

      // Diagnóstico: qué maquetado detectó y cuántas opciones maneja.
      this.setAttribute('data-ct-mode', this.combined ? 'combined' : 'per-option');
      this.setAttribute('data-ct-ready', 'true');
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }

    /** Las variantes que el propio elemento trae embebidas en JSON. */
    variants() {
      if (!this.variantData) {
        var json = this.querySelector('script[type="application/json"]');
        try {
          this.variantData = json ? JSON.parse(json.textContent) : [];
        } catch (error) {
          this.variantData = [];
        }
        if (!Array.isArray(this.variantData)) this.variantData = [];
      }
      return this.variantData;
    }

    /**
     * Valores de cada opción, en orden, deducidos del JSON de variantes.
     * Hace falta porque en el maquetado "por opción" el value de cada <option> es
     * un id de variante, no el valor de la opción, y su texto puede venir
     * traducido ("Talla L — agotado"), así que solo la POSICIÓN es fiable.
     */
    optionValueLists() {
      if (this.valueLists) return this.valueLists;
      var lists = [];
      this.variants().forEach(function (variant) {
        (variant.options || []).forEach(function (value, index) {
          if (!lists[index]) lists[index] = [];
          if (lists[index].indexOf(value) === -1) lists[index].push(value);
        });
      });
      this.valueLists = lists;
      return lists;
    }

    /** Valores de opción elegidos ahora mismo en ESTE selector. */
    readSelectedValues() {
      if (this.combined) {
        var raw = this.groups[0].control.value;
        return typeof raw === 'string' ? raw.split(',') : [];
      }
      var lists = this.optionValueLists();
      return this.groups.map(function (group, index) {
        var list = lists[index] || [];
        var position = selectedIndex(group);
        // Si el nº de valores no cuadra con el JSON, no adivinamos: devolvemos null
        // y esa opción se queda como está.
        if (position < 0 || group.items.length !== list.length) return null;
        return list[position];
      });
    }

    findVariant(values) {
      if (!values || !values.length) return null;
      return (
        this.variants().find(function (variant) {
          if (!Array.isArray(variant.options)) return false;
          return values.every(function (value, index) {
            return value == null || variant.options[index] === value;
          });
        }) || null
      );
    }

    onChange() {
      if (this.syncing) return;

      var values = this.readSelectedValues();
      var variant = this.findVariant(values);
      var picker = findMainPicker(this.sectionId);

      if (picker) {
        // Fuente de verdad única: se lo pasamos al picker de Dawn y que él haga todo
        // (precio, media, URL, input del formulario, disponibilidad y el pub/sub).
        var result = applyOptionValues(picker, values);
        if (result.changed && result.last) {
          result.last.dispatchEvent(new Event('change', { bubbles: true }));
          // Dawn ya resolvió la variante. Si no resolvió ninguna, esa combinación
          // no existe: él deja el botón bloqueado y nosotros no tocamos el id.
          variant = picker.currentVariant || null;
        }
      }

      if (!variant) return;
      commitVariantId(this.sectionId, variant.id);

      // Sin picker principal (producto de una sola variante, o bloque quitado)
      // no hay quien avise a la barra: la actualizamos nosotros con lo que sabemos.
      if (!picker) {
        var bar = findStickyBar(this.sectionId);
        if (bar && typeof bar.applyVariant === 'function') bar.applyVariant(variant, null);
      }
    }

    /** Camino de vuelta: el cliente cambió la variante arriba, la reflejamos aquí. */
    onVariantChange(payload) {
      var data = payload && payload.data;
      if (!data || !data.variant || data.sectionId !== this.sectionId) return;
      var options = data.variant.options;
      if (!Array.isArray(options)) return;

      // Escritura programática: NO se dispara `change`, así no hay bucle entre los
      // dos selectores. La bandera es el cinturón, además de los tirantes.
      this.syncing = true;
      try {
        if (this.combined) {
          var control = this.groups[0].control;
          var wanted = options.join(',');
          var exists = Array.from(control.options).some(function (option) {
            return option.value === wanted;
          });
          if (exists && control.value !== wanted) control.value = wanted;
        } else {
          var lists = this.optionValueLists();
          this.groups.forEach(function (group, index) {
            var list = lists[index] || [];
            var position = list.indexOf(options[index]);
            if (position < 0 || group.items.length !== list.length) return;
            if (group.type === 'select') {
              if (group.control.selectedIndex !== position) group.control.selectedIndex = position;
            } else if (!group.items[position].checked) {
              group.items[position].checked = true;
            }
          });
        }
      } finally {
        this.syncing = false;
      }
    }
  }

  /** Misma pieza, otra etiqueta: customElements exige un constructor distinto por nombre. */
  class SecondaryVariantSelectSeparate extends SecondaryVariantSelect {}

  /* ------------------------------------------------------------------ *
   * <internal-video> — vídeo de testimonio / slide
   * ------------------------------------------------------------------ */

  class InternalVideo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.video = this.querySelector('video');
      if (!this.video) {
        this.setAttribute('data-ct-ready', 'no-video');
        return;
      }

      this.playButton = this.querySelector('.internal-video__play');
      this.timeline = this.querySelector('.internal-video__timeline');
      this.soundButton = this.querySelector('.internal-video__sound-btn');

      // El Liquid marca las fuentes con `data-src` cuando el bloque pide carga
      // perezosa: hasta que no se ven, el vídeo no tiene ni una fuente que bajar.
      this.lazySources = Array.from(this.querySelectorAll('source[data-src]'));
      this.loaded = this.lazySources.length === 0;
      this.autoplay = this.dataset.autoplay === 'true' || this.video.hasAttribute('autoplay');

      this.bindControls();
      this.bindVideoEvents();
      this.syncState();
      this.observeViewport();

      // Diagnóstico: confirma desde la consola que el elemento se inicializó de verdad.
      this.setAttribute('data-ct-ready', 'true');
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    bindControls() {
      if (this.playButton) {
        // Un <button> sin type dentro de un formulario lo enviaría al pulsarlo.
        if (!this.playButton.getAttribute('type')) this.playButton.setAttribute('type', 'button');
        this.playButton.addEventListener('click', this.toggle.bind(this));
      }
      if (this.soundButton) {
        if (!this.soundButton.getAttribute('type')) this.soundButton.setAttribute('type', 'button');
        this.soundButton.addEventListener(
          'click',
          function () {
            this.video.muted = !this.video.muted;
          }.bind(this)
        );
      }
      if (this.timeline) {
        this.timeline.addEventListener('pointerdown', this.startSeek.bind(this));
        this.timeline.addEventListener('pointermove', this.moveSeek.bind(this));
        this.timeline.addEventListener('pointerup', this.endSeek.bind(this));
        this.timeline.addEventListener('pointercancel', this.endSeek.bind(this));
      }
    }

    bindVideoEvents() {
      var video = this.video;
      var self = this;
      video.addEventListener('play', function () {
        self.classList.add('internal-video--playing');
      });
      video.addEventListener('pause', function () {
        self.classList.remove('internal-video--playing');
      });
      video.addEventListener('ended', function () {
        self.classList.remove('internal-video--playing');
      });
      video.addEventListener('waiting', function () {
        self.classList.add('internal-video--loading');
      });
      video.addEventListener('playing', function () {
        self.classList.remove('internal-video--loading');
      });
      video.addEventListener('canplay', function () {
        self.classList.remove('internal-video--loading');
      });
      video.addEventListener('timeupdate', this.updateTimeline.bind(this));
      video.addEventListener('volumechange', this.syncMuted.bind(this));
    }

    /** El markup puede venir ya con las clases puestas: las alineamos con la realidad. */
    syncState() {
      this.classList.toggle('internal-video--playing', !this.video.paused);
      this.syncMuted();
      this.updateTimeline();
    }

    syncMuted() {
      this.classList.toggle('internal-video--muted', !!this.video.muted);
    }

    loadSources() {
      if (this.loaded) return;
      this.loaded = true;
      this.lazySources.forEach(function (source) {
        var src = source.getAttribute('data-src');
        if (!src) return;
        source.setAttribute('src', src);
        source.removeAttribute('data-src');
      });
      this.video.load();
    }

    observeViewport() {
      if (!('IntersectionObserver' in window)) {
        this.loadSources();
        return;
      }
      this.observer = new IntersectionObserver(this.onViewport.bind(this), { threshold: 0.25 });
      this.observer.observe(this);
    }

    onViewport(entries) {
      var entry = entries[entries.length - 1];
      if (entry.isIntersecting) {
        this.loadSources();
        // Autoplay solo si el markup lo pide y el cliente no ha pedido menos movimiento.
        if (this.autoplay && this.video.paused && !prefersReducedMotion()) {
          this.video.muted = true;
          this.play();
        }
        return;
      }
      // Fuera de pantalla paramos solo lo que arrancó solo; si el cliente le dio
      // al play a propósito, no se lo cortamos por hacer scroll.
      if (this.autoplay && !this.video.paused) this.video.pause();
    }

    play() {
      if (this.video.readyState < 3) this.classList.add('internal-video--loading');
      var promise = this.video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(
          function () {
            // El navegador puede bloquear la reproducción (autoplay sin gesto): no es un error nuestro.
            this.classList.remove('internal-video--loading');
            this.classList.remove('internal-video--playing');
          }.bind(this)
        );
      }
    }

    toggle() {
      this.loadSources();
      if (this.video.paused) this.play();
      else this.video.pause();
    }

    updateTimeline() {
      if (!this.timeline) return;
      var duration = this.video.duration;
      if (!isFinite(duration) || duration <= 0) return;
      var percent = (this.video.currentTime / duration) * 100;
      this.timeline.style.setProperty('--completed', percent.toFixed(2) + '%');
    }

    startSeek(event) {
      if (!this.timeline) return;
      event.preventDefault();
      this.loadSources();
      this.seeking = true;
      if (this.timeline.setPointerCapture) {
        try {
          this.timeline.setPointerCapture(event.pointerId);
        } catch (error) {
          /* algunos navegadores lo rechazan; se sigue igual sin capturar */
        }
      }
      this.seekTo(event);
    }

    moveSeek(event) {
      if (this.seeking) this.seekTo(event);
    }

    endSeek(event) {
      this.seeking = false;
      if (this.timeline && this.timeline.releasePointerCapture && event.pointerId != null) {
        try {
          this.timeline.releasePointerCapture(event.pointerId);
        } catch (error) {
          /* ya estaba liberado */
        }
      }
    }

    seekTo(event) {
      var duration = this.video.duration;
      if (!isFinite(duration) || duration <= 0) return;
      var rect = this.timeline.getBoundingClientRect();
      if (!rect.width) return;
      var ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      this.video.currentTime = ratio * duration;
      this.updateTimeline();
    }
  }

  /* ------------------------------------------------------------------ *
   * Registro — siempre con guard, nunca se redefine nada de Dawn.
   * ------------------------------------------------------------------ */

  function define(name, constructor) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  }

  define('sticky-atc', StickyAtc);
  define('secondary-variant-select', SecondaryVariantSelect);
  define('secondary-variant-select-separate', SecondaryVariantSelectSeparate);
  define('internal-video', InternalVideo);
})();
